import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ApplyToPageSelectionDialog } from './apply-to-page-selection-dialog';
import { EndBlockDocsActionsMenu, EndBlockDocsList } from './end-block-docs-list';
import { EndBlockFactsActionsMenu, EndBlockFactsList } from './end-block-facts-list';
import { KimFeedbackDialog } from './kim-feedback-dialog';
import { FeedbackQuestionIds } from '../../../../../../../../../../../backend/src/feedback/feedback-questions';
import { Permission } from '../../../../../../../../../../../backend/src/shared/action-registry';
import { ArtifactType, ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { TemplateBuilderDialog } from '../../template-builder/template-builder-dialog';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useAddDocsToDocChronology } from '@/api/mutations/useAddDocsToDocChronology';
import { useCreateChronology } from '@/api/mutations/useCreateChronology';
import { useCreateDocChronology } from '@/api/mutations/useCreateDocChronology';
import { useCreateDocumentZip } from '@/api/mutations/useCreateDocumentZip';
import useExportBundle from '@/api/mutations/useExportBundle';
import { useGenerateTemplateFromMessage } from '@/api/mutations/useGenerateTemplateFromMessage';
import { useSubmitFeedback } from '@/api/mutations/useSubmitFeedback';
import { useUpdateFeedback } from '@/api/mutations/useUpdateFeedback';
import { useGetChronologiesForSubset } from '@/api/queries/useGetChronologiesForSubset';
import { useGetChunksBatch } from '@/api/queries/useGetChunksBatch';
import { useGetDocBundleItems } from '@/api/queries/useGetDocBundleItems';
import { useGetDocChronologies } from '@/api/queries/useGetDocChronologies';
import { useGetDocsBatch } from '@/api/queries/useGetDocsBatch';
import { useGetFactDetailsBatch } from '@/api/queries/useGetFactDetailsBatch';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { FactDetails } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { AddToSubsetDialog } from '@/new/components/add-to-subset-dialog';
import { CopyIconButton } from '@/new/components/copy-icon-button';
import { DownloadFlow } from '@/new/components/download/DownloadFlow';
import { LARGE_BUNDLE_BLOCK_THRESHOLD } from '@/new/components/download/fields/large-export-warning';
import type { BundleVariant, DownloadDefinition, ZipOption } from '@/new/components/download/types';
import { useDownloadController } from '@/new/components/download/useDownloadController';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';
import { addFactsBatchToVisualisation } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/components/visual-timeline/syncing-helpers/syncVisualisationWithTimelineChanges';
import { AddToDocChronologyDialog } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/doc/components/add-to-doc-chronology-dialog';
import { CreateDocChronologyDialog } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/doc/components/create-doc-chronology-dialog';
import { AddToChronologyDialog } from '@/new/pages/matters/_matterId/subsets/_subsetId/facts/components/dialogs/add-to-chronology-dialog';

type ActiveTab = 'facts' | 'docs' | null;

function useDocBundleItems({
  matterId,
  subsetId,
  selectedDocIds,
}: {
  matterId: string;
  subsetId: string;
  selectedDocIds: Set<string>;
}) {
  // sort for stable reference
  const docIds = useMemo(() => Array.from(selectedDocIds).sort(), [selectedDocIds]);
  const { data, isLoading, isError } = useGetDocBundleItems({ matterId, subsetId, docIds });
  const items = (data ?? []).map((entry) => ({
    id: entry.docId,
    schemaName: 'doc' as const,
    schemaId: entry.docId,
    originalName: entry.fileName,
    pageCount: entry.pageCount ?? undefined,
    dateText: entry.dateText,
  }));
  return { items, isLoading, isError };
}

function buildDocMeta(facts: FactDetails[]) {
  const map = new Map<string, { name: string; type: string | null }>();
  for (const fact of facts) {
    for (const location of fact.locations) {
      if (location.doc && !map.has(location.docId)) {
        map.set(location.docId, {
          name: location.doc.file?.name ?? location.docId,
          type: location.doc.type ?? null,
        });
      }
    }
  }
  return map;
}

export function EndBlock({
  messageId,
  getTextToCopy,
  getHtmlToCopy,
  factIds,
  chunkIds = [],
  directDocIds = [],
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  isDownloading,
  isChronology = false,
  hasTable = false,
  artifactTypes = [],
  wordCount = 0,
  threadFileChunkRefCount = 0,
}: {
  messageId?: string;
  getTextToCopy: () => string;
  getHtmlToCopy?: () => string;
  factIds: string[];
  chunkIds?: string[];
  directDocIds?: string[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: () => void;
  isDownloading?: boolean;
  isChronology?: boolean;
  hasTable?: boolean;
  artifactTypes?: ArtifactType[];
  wordCount?: number;
  threadFileChunkRefCount?: number;
}) {
  const { matterId, subsetId, threadId } = useParams();
  const navigate = useNavigate();
  const authInfo = useAuthInfo();

  const [activeTab, setActiveTab] = useState<ActiveTab>(null);
  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set());
  const hasInitializedFactSelection = useRef(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const hasInitializedDocSelection = useRef(false);
  const [templateDraft, setTemplateDraft] = useState<{ title: string; body: string } | null>(null);

  const [isAddToChronologyOpen, setIsAddToChronologyOpen] = useState(false);
  const [isCreateDocChronOpen, setIsCreateDocChronOpen] = useState(false);
  const [isAddToDocChronOpen, setIsAddToDocChronOpen] = useState(false);
  const [isAddToSubsetOpen, setIsAddToSubsetOpen] = useState(false);
  const [feedback, setFeedback] = useState<{ id: string; response: 'thumbs-up' | 'thumbs-down' } | null>(null);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const { submitFeedback } = useSubmitFeedback();
  const { updateFeedback } = useUpdateFeedback();
  const [downloadKind, setDownloadKind] = useState<'zip' | 'bundle'>('zip');
  const [applyToSelectionTarget, setApplyToSelectionTarget] = useState<{
    itemNoun: 'fact' | 'document';
    ids: string[];
    currentCount: number;
  } | null>(null);

  const { isLoading: isFactsLoading, data: factsData } = useGetFactDetailsBatch(matterId, subsetId, factIds);
  const facts = factsData ?? [];

  const { isLoading: isChunksLoading, isError: isChunksError, data: chunks } = useGetChunksBatch(matterId, chunkIds);

  const isLoading = isFactsLoading || isChunksLoading;
  const hasReferences = factIds.length > 0 || chunkIds.length > 0 || directDocIds.length > 0;
  const hasResolvableReferences = hasReferences && !(chunkIds.length > 0 && isChunksError && chunks.length === 0);

  const { mutate: generateTemplate, isLoading: isGeneratingTemplate } = useGenerateTemplateFromMessage(matterId);

  // Select only the facts that resolve: a referenced id the batch does not return is not shown,
  // and the server rejects a chronology that includes it.
  useEffect(() => {
    if (hasInitializedFactSelection.current || isFactsLoading || facts.length === 0) return;
    setSelectedFactIds(new Set(facts.map((fact) => fact.id)));
    hasInitializedFactSelection.current = true;
  }, [facts, isFactsLoading]);

  const docIds = useMemo(() => {
    const ids = new Set<string>();
    for (const fact of facts) {
      for (const location of fact.locations) {
        ids.add(location.docId);
      }
    }
    for (const chunk of chunks) {
      ids.add(chunk.docId);
    }
    for (const docId of directDocIds) {
      ids.add(docId);
    }
    return Array.from(ids);
  }, [facts, chunks, directDocIds]);

  useEffect(() => {
    if (hasInitializedDocSelection.current || isLoading || docIds.length === 0) return;
    setSelectedDocIds(new Set(docIds));
    hasInitializedDocSelection.current = true;
  }, [docIds, isLoading]);

  const { data: fetchedDocs } = useGetDocsBatch(matterId, docIds);

  const docMeta = useMemo(() => {
    const map = buildDocMeta(facts);
    for (const doc of fetchedDocs ?? []) {
      map.set(doc.id, { name: doc.name, type: doc.type });
    }
    return map;
  }, [facts, fetchedDocs]);

  const selectedFactIdsArray = useMemo(() => Array.from(selectedFactIds), [selectedFactIds]);
  const selectedDocIdsArray = useMemo(() => Array.from(selectedDocIds), [selectedDocIds]);

  const {
    selectMultipleFacts,
    deselectAllFacts,
    getAllSelectedFactIds,
    selectMultipleDocuments,
    deselectAllDocuments,
    getAllSelectedDocumentIds,
  } = useIndexedDb();

  const { createChronology } = useCreateChronology({
    matterId: matterId ?? '',
    subsetId: subsetId ?? '',
    onSuccess: (data) => {
      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/${data.chronologyId}`, { replace: false });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { data: chronologies } = useGetChronologiesForSubset({
    matterId: matterId ?? '',
    subsetId: subsetId ?? '',
  });

  const { data: docChronologies = [], isLoading: isLoadingDocChronologies } = useGetDocChronologies({
    matterId: matterId ?? '',
    subsetId: subsetId ?? '',
  });

  const createDocChronology = useCreateDocChronology({
    onSuccess: (data) => {
      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${data.id}`);
    },
  });

  const addDocsToDocChronology = useAddDocsToDocChronology();

  const { createZipAsync } = useCreateDocumentZip({ matterId: matterId ?? '', subsetId: subsetId ?? '' });
  const { bundleEndpoints } = useExportBundle();
  const { data: subset } = useGetSubset(matterId, subsetId);
  const downloadController = useDownloadController();

  const handleGenerateTemplate = () => {
    if (!messageId) return;
    generateTemplate(
      { messageId },
      {
        onSuccess: (draft) => {
          trackCustomEvent(TrackingEventsV2.KimGenerateTemplateFromMessage, { matterId, subsetId, messageId });
          setTemplateDraft(draft);
        },
        onError: (error) => {
          toast.error(error.message || 'Error generating template. Please try again.');
        },
      },
    );
  };

  const handleTabClick = (tab: 'facts' | 'docs') => {
    setActiveTab((prev) => (prev === tab ? null : tab));
  };

  const handleThumbsClick = (response: 'thumbs-up' | 'thumbs-down') => {
    if (!messageId) return;

    if (feedback && feedback.response === response) {
      setIsFeedbackDialogOpen(true);
      return;
    }

    if (feedback) {
      updateFeedback.mutate(
        { id: feedback.id, response },
        {
          onSuccess: () => {
            setFeedback({ id: feedback.id, response });
            setIsFeedbackDialogOpen(true);
          },
          onError: () => {
            toast.error('Failed to update feedback. Please try again.');
          },
        },
      );
      return;
    }

    submitFeedback.mutate(
      {
        questionId: FeedbackQuestionIds.KimAnswerAccuracy,
        response,
        metadata: {
          matterId,
          subsetId,
          threadId,
          messageId,
          factCount: factIds.length,
          chunkCount: chunkIds.length,
          directDocCount: directDocIds.length,
          uniqueDocCount: docIds.length,
          hasTable,
          hasArtifact: artifactTypes.length > 0,
          artifactTypes,
          isChronology,
          wordCount,
          threadFileChunkRefCount,
        },
      },
      {
        onSuccess: (data: { id: string }) => {
          setFeedback({ id: data.id, response });
          setIsFeedbackDialogOpen(true);
        },
        onError: () => {
          toast.error('Failed to submit feedback. Please try again.');
        },
      },
    );
  };

  const handleCreateFactChronology = () => {
    if (selectedFactIdsArray.length === 0) return;
    createChronology.mutate({ chronologyId: null, factIds: selectedFactIdsArray, titlePrefix: 'From Kim: ' });
  };

  const handleSelectChronology = async (chronologyId: string) => {
    if (selectedFactIdsArray.length === 0) return;
    setIsAddToChronologyOpen(false);
    try {
      const result = await createChronology.mutateAsync({
        chronologyId,
        factIds: selectedFactIdsArray,
      });
      if (result.addedFacts && result.addedFacts.length > 0 && matterId && subsetId) {
        await addFactsBatchToVisualisation({
          matterId,
          subsetId,
          chronologyId,
          newFacts: result.addedFacts,
          getAccessToken: authInfo.tokens.getAccessToken,
        });
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to add facts to chronology');
    }
  };

  const handleSelectOnFactsPage = async () => {
    if (!subsetId || selectedFactIdsArray.length === 0) return;
    const currentIds = await getAllSelectedFactIds(subsetId);
    setApplyToSelectionTarget({ itemNoun: 'fact', ids: selectedFactIdsArray, currentCount: currentIds.length });
  };

  const handleCreateDocChronology = (options: {
    includeKeyFacts: boolean;
    includeRelevantFacts: boolean;
    includeAllFacts: boolean;
  }) => {
    if (!matterId || !subsetId || selectedDocIdsArray.length === 0) return;
    createDocChronology.mutate({
      matterId,
      subsetId,
      docIds: selectedDocIdsArray,
      includeKeyFacts: options.includeKeyFacts,
      includeRelevantFacts: options.includeRelevantFacts,
      includeAllFacts: options.includeAllFacts,
    });
  };

  const handleSelectDocChronology = (docChronologyId: string) => {
    if (!matterId || !subsetId || selectedDocIdsArray.length === 0) return;
    addDocsToDocChronology.mutate(
      { matterId, subsetId, docChronologyId, docIds: selectedDocIdsArray },
      {
        onSuccess: () => {
          navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${docChronologyId}`);
        },
      },
    );
  };

  const handleCreateNewSubset = () => {
    if (!matterId || selectedDocIdsArray.length === 0) return;
    navigate(`/matters/${matterId}/subsets/new?documentIds=${selectedDocIdsArray.join(',')}`);
  };

  const handleSelectOnDocsPage = async () => {
    if (!subsetId || selectedDocIdsArray.length === 0) return;
    const currentIds = await getAllSelectedDocumentIds(subsetId);
    setApplyToSelectionTarget({ itemNoun: 'document', ids: selectedDocIdsArray, currentCount: currentIds.length });
  };

  const handleApplyToSelectionConfirm = async (mode: 'add' | 'replace') => {
    if (!applyToSelectionTarget || !subsetId) return;
    const { itemNoun, ids } = applyToSelectionTarget;
    if (itemNoun === 'fact') {
      if (mode === 'replace') await deselectAllFacts(subsetId);
      await selectMultipleFacts(subsetId, ids);
      const verb = mode === 'replace' ? 'Replaced' : 'Added';
      const noun = ids.length === 1 ? 'fact' : 'facts';
      toast.success(`${verb} ${ids.length} ${noun} on the facts page`);
    } else {
      if (mode === 'replace') await deselectAllDocuments(subsetId);
      await selectMultipleDocuments(subsetId, ids);
      const verb = mode === 'replace' ? 'Replaced' : 'Added';
      const noun = ids.length === 1 ? 'document' : 'documents';
      toast.success(`${verb} ${ids.length} ${noun} on the docs page`);
    }
    setApplyToSelectionTarget(null);
  };

  const selectedDocIdsForBundle = useMemo(() => new Set(selectedDocIdsArray), [selectedDocIdsArray]);

  const downloadDefinition: DownloadDefinition = useMemo(() => {
    const count = selectedDocIdsArray.length;
    const subsetName = 'Selected Documents';
    const bundleDefaultName = `${subset?.name ?? 'Documents'} Bundle`;

    const bundleVariant: BundleVariant = {
      id: 'selected-docs-bundle',
      kind: 'bundle',
      label: 'PDF Bundle',
      description: 'Paginated PDFs from selected documents',
      disabledReason:
        count === 0
          ? 'Select one or more documents to bundle.'
          : count > LARGE_BUNDLE_BLOCK_THRESHOLD
            ? `Bundles are limited to ${LARGE_BUNDLE_BLOCK_THRESHOLD.toLocaleString()} documents (you have ${count.toLocaleString()} selected).`
            : undefined,
      bundle: {
        matterId: matterId ?? '',
        subsetId: subsetId ?? '',
        itemType: 'doc',
        useItems: () =>
          useDocBundleItems({
            matterId: matterId ?? '',
            subsetId: subsetId ?? '',
            selectedDocIds: selectedDocIdsForBundle,
          }),
        defaultName: bundleDefaultName,
      },
      ...bundleEndpoints(matterId ?? '', subsetId ?? ''),
      readyDescription: 'Your PDF bundle is ready.',
    };

    const zipVariant = {
      id: 'selected-docs-zip',
      kind: 'zip' as const,
      label: 'Zip',
      description: 'Archive of selected documents.',
      mode: 'async' as const,
      formats: ['zip' as const],
      defaultFilename: subsetName,
      showZipContent: true,
      largeExportWarning: true,
      mutate: async (values: { scope?: string; zipContent?: ZipOption; filename?: string }) => {
        const data = await createZipAsync({
          docIds: selectedDocIdsArray,
          zipContent: values.zipContent,
          title: values.filename || subsetName,
        });
        if (!data.exportId) throw new Error('Failed to start zip creation');
        return { exportId: data.exportId, jobId: data.jobId };
      },
      progressEndpoint: () => `${APIBaseChronos}/api/matter/${matterId}/docs/zip-progress/${subsetId}`,
      statusEndpoint: () => `${APIBaseChronos}/api/matter/${matterId}/docs/zip-status/${subsetId}`,
      readyDescription: 'Selected documents have been packaged as a zip.',
    };

    return {
      title: downloadKind === 'bundle' ? 'Download PDF Bundle' : 'Download Selected Documents',
      scopes: [
        {
          id: 'selected',
          label: `Download ${count} selected document${count === 1 ? '' : 's'}`,
          totalCount: count,
        },
      ],
      defaultScopeId: 'selected',
      variants: downloadKind === 'bundle' ? [bundleVariant] : [zipVariant],
    };
  }, [
    selectedDocIdsArray,
    selectedDocIdsForBundle,
    matterId,
    subsetId,
    subset?.name,
    createZipAsync,
    bundleEndpoints,
    downloadKind,
  ]);

  const openDownload = (kind: 'zip' | 'bundle') => {
    setDownloadKind(kind);
    downloadController.open();
  };

  return (
    <div>
      <motion.div
        className="mx-auto mt-6 flex w-full max-w-3xl items-center justify-between gap-4 px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15, delay: 0.2 }}
      >
        <div className="my-1 flex gap-0.5 pb-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <CopyIconButton getText={getTextToCopy} getHtml={getHtmlToCopy} />
            </TooltipTrigger>

            <TooltipContent>Copy answer to clipboard</TooltipContent>
          </Tooltip>

          <PermissionGate
            permission={Permission.ThreadDownload}
            uiMode="disabled"
            disabledMessage="You don't have permission to download an answer"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onDownloadClick?.()}
                  disabled={isDownloading}
                  variant="ghost"
                  className="p-1 px-1.5"
                >
                  <Icon className="lucide--arrow-down-to-line" />
                </Button>
              </TooltipTrigger>

              <TooltipContent>Download as Word document</TooltipContent>
            </Tooltip>
          </PermissionGate>

          {messageId && hasReferences && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleGenerateTemplate}
                  disabled={isGeneratingTemplate}
                  variant="ghost"
                  className="p-1 px-1.5"
                >
                  <AnimatePresence mode="wait">
                    {isGeneratingTemplate ? (
                      <motion.span
                        key="loader"
                        initial={{ opacity: 0, filter: 'blur(2px)' }}
                        animate={{ opacity: 1, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, filter: 'blur(2px)' }}
                        transition={{ duration: 0.15, ease: 'easeInOut' }}
                      >
                        <Icon className="lucide--loader animate-spin ease-in-out" />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="template"
                        initial={{ opacity: 0, filter: 'blur(2px)' }}
                        animate={{ opacity: 1, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, filter: 'blur(2px)' }}
                        transition={{ duration: 0.15, ease: 'easeInOut' }}
                      >
                        <Icon className="lucide--layout-template" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </TooltipTrigger>

              <TooltipContent>Save as a reusable template</TooltipContent>
            </Tooltip>
          )}

          {messageId && (
            <>
              <Button
                variant="ghost"
                className={cn('p-1 px-1.5', feedback?.response === 'thumbs-up' && 'text-teal-500')}
                onClick={() => handleThumbsClick('thumbs-up')}
                disabled={submitFeedback.isLoading || updateFeedback.isLoading}
              >
                <Icon className="lucide--thumbs-up" />
              </Button>
              <Button
                variant="ghost"
                className={cn('p-1 px-1.5', feedback?.response === 'thumbs-down' && 'text-teal-500')}
                onClick={() => handleThumbsClick('thumbs-down')}
                disabled={submitFeedback.isLoading || updateFeedback.isLoading}
              >
                <Icon className="lucide--thumbs-down" />
              </Button>
            </>
          )}
        </div>

        <AnimatePresence>
          {hasResolvableReferences && !isLoading && matterId && subsetId && (
            <motion.div
              className="flex flex-wrap items-center justify-end gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
            >
              <div className="flex items-center gap-2">
                {isChronology && (
                  <FactChronologyDropdown
                    matterId={matterId}
                    subsetId={subsetId}
                    selectedFactIds={selectedFactIds}
                    createTitlePrefix="From Kim: "
                    triggerText="Save as a chronology"
                    size="sm"
                    variant="white"
                  />
                )}

                {(factIds.length > 0 || docIds.length > 0) && (
                  <Tabs value={activeTab ?? ''}>
                    <TabsList variant="pill">
                      {factIds.length > 0 && (
                        <TabsTrigger value="facts" onClick={() => handleTabClick('facts')}>
                          <Icon className="lucide--search" />
                          Fact references
                        </TabsTrigger>
                      )}

                      {docIds.length > 0 && (
                        <TabsTrigger value="docs" onClick={() => handleTabClick('docs')}>
                          <Icon className="lucide--book" />
                          Cited documents
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {activeTab === 'facts' && matterId && subsetId && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mx-auto max-w-3xl overflow-hidden px-px"
          >
            <EndBlockFactsList
              subsetId={subsetId}
              facts={facts}
              isLoading={isFactsLoading}
              factIds={factIds}
              selectedFactIds={selectedFactIds}
              setSelectedFactIds={setSelectedFactIds}
              referenceIndexMap={referenceIndexMap}
              onReferenceClick={onReferenceClick}
              actionsMenu={
                <EndBlockFactsActionsMenu
                  onCreateChronology={handleCreateFactChronology}
                  onAddToExistingChronology={() => setIsAddToChronologyOpen(true)}
                  onSelectOnPage={handleSelectOnFactsPage}
                  disabled={selectedFactIdsArray.length === 0}
                  isCreatingChronology={createChronology.isLoading}
                  isChronology={isChronology}
                />
              }
            />
          </motion.div>
        )}

        {activeTab === 'docs' && matterId && subsetId && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mx-auto max-w-3xl overflow-hidden px-px"
          >
            <EndBlockDocsList
              docIds={docIds}
              docMeta={docMeta}
              selectedDocIds={selectedDocIds}
              setSelectedDocIds={setSelectedDocIds}
              onDocClick={(docId) => onReferenceClick?.(docId, ReferenceType.Document)}
              actionsMenu={
                <EndBlockDocsActionsMenu
                  onCreateDocChronology={() => setIsCreateDocChronOpen(true)}
                  onAddToExistingDocChronology={() => setIsAddToDocChronOpen(true)}
                  onCreateSubset={handleCreateNewSubset}
                  onAddToExistingSubset={() => setIsAddToSubsetOpen(true)}
                  onSelectOnPage={handleSelectOnDocsPage}
                  onDownloadZip={() => openDownload('zip')}
                  onDownloadBundle={() => openDownload('bundle')}
                  disabled={selectedDocIdsArray.length === 0}
                  isCreatingDocChronology={createDocChronology.isLoading || addDocsToDocChronology.isLoading}
                />
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      <TemplateBuilderDialog
        open={!!templateDraft}
        onOpenChange={(open) => {
          if (!open) setTemplateDraft(null);
        }}
        initialDraft={templateDraft ?? undefined}
      />

      <AddToChronologyDialog
        isOpen={isAddToChronologyOpen}
        onOpenChange={setIsAddToChronologyOpen}
        isLoadingChronologies={false}
        chronologies={chronologies ?? []}
        handleSelectChronology={handleSelectChronology}
        isAddingFacts={createChronology.isLoading}
      />

      <CreateDocChronologyDialog
        isOpen={isCreateDocChronOpen}
        onOpenChange={setIsCreateDocChronOpen}
        onConfirm={(opts) => {
          setIsCreateDocChronOpen(false);
          handleCreateDocChronology(opts);
        }}
        isCreating={createDocChronology.isLoading}
        description={`Create a new document chronology from ${selectedDocIdsArray.length} selected ${
          selectedDocIdsArray.length === 1 ? 'document' : 'documents'
        }.`}
      />

      <AddToDocChronologyDialog
        isOpen={isAddToDocChronOpen}
        onOpenChange={setIsAddToDocChronOpen}
        isLoadingDocChronologies={isLoadingDocChronologies}
        docChronologies={docChronologies}
        handleSelectDocChronology={(id) => {
          setIsAddToDocChronOpen(false);
          handleSelectDocChronology(id);
        }}
        isAddingDocs={addDocsToDocChronology.isLoading}
        description={`Add ${selectedDocIdsArray.length} selected ${
          selectedDocIdsArray.length === 1 ? 'document' : 'documents'
        } to an existing document chronology.`}
      />

      {matterId && subsetId && (
        <AddToSubsetDialog
          isOpen={isAddToSubsetOpen}
          onOpenChange={setIsAddToSubsetOpen}
          matterId={matterId}
          currentSubsetId={subsetId}
          docIds={selectedDocIdsArray}
          docDescription={`selected ${selectedDocIdsArray.length === 1 ? 'document' : 'documents'}`}
        />
      )}

      {applyToSelectionTarget && (
        <ApplyToPageSelectionDialog
          isOpen={!!applyToSelectionTarget}
          onOpenChange={(open) => {
            if (!open) setApplyToSelectionTarget(null);
          }}
          itemNoun={applyToSelectionTarget.itemNoun}
          currentSelectionCount={applyToSelectionTarget.currentCount}
          newItemCount={applyToSelectionTarget.ids.length}
          onConfirm={handleApplyToSelectionConfirm}
        />
      )}

      <DownloadFlow controller={downloadController} definition={downloadDefinition} />

      <KimFeedbackDialog
        open={isFeedbackDialogOpen}
        onOpenChange={setIsFeedbackDialogOpen}
        feedbackId={feedback?.id ?? null}
      />
    </div>
  );
}
